import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { plainMap } from '@ipfabric/utils/fp/plainMap';
import { pipe } from 'fp-ts/lib/function';
import { useMemo } from 'react';

type MemoEntry<TValue> = { value: TValue; used: boolean };

/**
 * Memoize a list of values, each individually
 *
 * Example:
 * ```
 * const Item = memo(props => <div>{props.path.join(',')}</div>);
 *
 * // props.parentPath is number[]
 * const getItemPath = useMemoCollection(
 *   (index: number) => [...props.parentPath, index],
 *   [props.parentPath]
 * );
 *
 * return (
 *   <>
 *     {items.map((item, index) => <Item path={getItemPath(index)} />)}
 *   </>
 * );
 * ```
 *
 * itemPath(index) provides values with stable identity for each individual key so Item can be
 * easily memoed.
 *
 * The value factory identity (itemPath function) is also stable.
 *
 * On every render, the hook purges values under keys that weren't unused in last render to prevent
 * memory leaks. This means that when an item in the list is unmounted and remounted later,
 * the value for that item will lose its original identity. But in that case there is no memoization
 * of that component anyway.
 */
export function useMemoCollection<TKey, TValue>(
  getValue: (key: TKey) => TValue,
  deps: unknown[],
): (key: TKey) => TValue {
  const valuesMap = useMemo(() => new Map<TKey, MemoEntry<TValue>>(), deps);
  // clear unused entries from the previous render
  pipe(
    valuesMap,
    plainMap.filterMutable(({ used }) => used),
  );
  // mark remaining fields unused
  // biome-ignore lint/complexity/noForEach: Map
  valuesMap.forEach((entry) => {
    entry.used = false;
  });

  return useStableCallback((key) => {
    const mapEntry = pipe(
      valuesMap,
      plainMap.getOrCreate<TKey, MemoEntry<TValue>>(key, () => ({
        value: getValue(key),
        used: true,
      })),
    );
    mapEntry.used = true;
    return mapEntry.value;
  });
}
